import { useCookieContext } from 'context';
import React, { useEffect, useState } from 'react';

import './AgentExecution.scss';
import { useTypewriter } from '../useTypewriter';

import { useMessagesContext } from 'components/chat/MessagesContext';

interface AgentExecutionProps {
  userInput: string;
  isUser: boolean;
  onContentUpdate: () => void; // Added this line
}

let sessionId: string | null = null;

function GetSessionId(): string {
  if (!sessionId) {
    sessionId = GenerateSessionId();
  }
  return sessionId;
}

function GenerateSessionId(): string {
  // Generate a random alphanumeric string
  return Math.random().toString(36).substr(2, 9);
}

// Refactor to ENV
function GetGatewayURL() {
  const hostname = window.location.hostname;
  const environmentMappings = {
    'goat-stage': 'https://gateway.stg.demandbase.com',
    'goat-dev': 'https://gateway.gdpr-dev.demandbase.com',
    'goat-prod': 'https://gateway.demandbase.com',
  };
  for (const [env, targetUrl] of Object.entries(environmentMappings)) {
    if (hostname.includes(env)) {
      return targetUrl;
    }
  }

  return environmentMappings['goat-prod'];
}

const GATEWAY_URL = GetGatewayURL();

const AgentExecution = ({
  userInput,
  isUser,
  onContentUpdate, // Added this line
}: AgentExecutionProps): React.ReactElement => {
  const [actions, setActions] = useState<{ text: string; status: 'executing' | 'executed' }[]>([]);
  const [finalResponse, setFinalResponse] = useState<string | null>(null);
  const [references, setReferences] = useState<any[]>([]);
  const [waitingForFirstEvent, setWaitingForFirstEvent] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { cookie } = useCookieContext();
  // Use the typewriter effect for the final response
  const [displayedFinalResponse, isDone] = useTypewriter(finalResponse ?? '');
  const { setAgentExecuting } = useMessagesContext();

  useEffect(() => {
    // Scroll to bottom whenever content updates
    if (onContentUpdate) {
      onContentUpdate();
    }
  }, [displayedFinalResponse, actions, waitingForFirstEvent, error]);

  useEffect(() => {
    let isMounted = true;

    // Function to process each invocation as it comes in
    const processInvocation = (invocation: string) => {
      const parsedInvocation = JSON.parse(invocation);

      // Remove the initial spinning wheel once we receive the first event
      if (waitingForFirstEvent) {
        setWaitingForFirstEvent(false);
      }

      // Display agentInvocationInput entries
      if (parsedInvocation.agentInvocationInput && isMounted) {
        const actionText = parsedInvocation.agentInvocationInput.apiPath;
        setActions((prev) => [...prev, { text: actionText, status: 'executing' }]);
      }

      // Handle finalResponse or observation to mark the previous action as executed
      if ((parsedInvocation.finalResponse || parsedInvocation.observation) && isMounted) {
        setActions((prev) =>
          prev.map((action, index) =>
            index === prev.length - 1 ? { ...action, status: 'executed' } : action
          )
        );
      }

      // Display the final response message
      if (parsedInvocation.finalResponse && isMounted) {
        setFinalResponse(parsedInvocation.finalResponse);
      }

      // Support bot response
      if (parsedInvocation.success.body.output && isMounted) {
        const finalResponse = parsedInvocation.success.body.output;
        const references = parsedInvocation.success.body.refs || [];
        setReferences(references);
        setFinalResponse(finalResponse);
      }
    };

    const parseCookies = (cookie: any) => {
      return cookie.split(';').reduce((cookies: any, cookie: string) => {
        const [key, value] = cookie.trim().split('=');
        cookies[key] = decodeURIComponent(value);
        return cookies;
      }, {});
    };

    // Function to connect to the backend and process the streaming response
    const fetchAgentStream = async () => {
      const cookies = parseCookies(cookie || document.cookie);
      setAgentExecuting(true);
      try {
        const bearer = (cookies && cookies['bearer']) || '';

        const response = await fetch(`${GATEWAY_URL}/support-agent/api/v0/supportDocBotStream`, {
          method: 'POST',
          // credentials: 'include',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Authorization: bearer,
            'X-Request-Id': crypto.randomUUID(),
          },
          // Inside fetchAgentStream function
          body: JSON.stringify({
            query: userInput,
          }),
        });

        if (!response.body) {
          setError('ReadableStream not supported in this browser.');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let { value, done } = await reader.read();

        while (!done && isMounted) {
          const chunk = decoder.decode(value, { stream: true });
          // Split the chunk into individual JSON strings using newline as the delimiter
          const invocations = chunk.trim().split(/\r?\n/);
          // Process each invocation separately
          invocations.forEach((invocation) => {
            processInvocation(invocation);
          });
          // Read the next chunk
          ({ value, done } = await reader.read());
        }
      } catch (error) {
        setError('Error fetching agent stream.');
      } finally {
        setAgentExecuting(false);
      }
    };

    fetchAgentStream();

    return () => {
      isMounted = false;
    };
  }, [userInput]);

  return (
    <>
      {error ? (
        <div className='error-alert'>
          <p>{error}</p>
        </div>
      ) : (
        <div className={`message-bubble ${isUser ? 'user-message' : 'system-message'}`}>
          {/* Show the initial spinning wheel while waiting for the first event */}
          {waitingForFirstEvent && (
            <div className='agent-current-action'>
              <span className='spinner'></span> Waiting for the response...
            </div>
          )}

          {/* Display each action with its status */}
          {actions.map((action, index) => (
            <div key={index} className='agent-action'>
              {action.status === 'executing' ? (
                <div className='agent-current-action'>
                  <span className='spinner'></span> Executing: {action.text}
                </div>
              ) : (
                <div className='agent-completed-action'>
                  <span className='checkmark'>✔️</span> Executed: {action.text}
                </div>
              )}
            </div>
          ))}

          {/* Display the final response with the typewriter effect if available */}
          {finalResponse && (
            <div className='agent-final-response'>
              <p>{displayedFinalResponse}</p>
              {isDone && references.length > 0 && (
                <ul id='ref-list-id'>
                  {references.map((reference: any, index: number) => (
                    <li key={index}>
                      <a href={reference.url} target='_blank' rel='noreferrer'>
                        {reference.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AgentExecution;
